import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import api from "../../lib/api";
import { toast } from "sonner";
import { X, ClipboardText, CheckCircle, XCircle } from "@phosphor-icons/react";
import { API } from "../../App";

const REASON_LABELS = {
  current_bde_unable: "BDE unable to handle",
  location_mismatch: "Location mismatch",
  customer_requested_different: "Customer requested different",
  workload_balancing: "Workload balancing",
  manager_reassignment: "Manager reassignment",
  follow_up_not_done: "Follow-up not done",
  leave_absence: "Leave / Absence",
  resignation_handover: "Resignation handover",
  product_category_mismatch: "Product category mismatch",
  duplicate_assignment: "Duplicate assignment",
  other: "Other",
};

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
};

export default function TransferApprovalsDrawer({ open, user, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");                 // request_id currently processing
  const [statusFilter, setStatusFilter] = useState("pending");
  const [decisionNotes, setDecisionNotes] = useState({}); // request_id -> note

  const role = (user?.role || "").toLowerCase();

  const fetchList = async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`${API}/leads/transfer/requests?${params.toString()}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load requests");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, statusFilter]);

  const decide = async (request_id, action) => {
    if (!window.confirm(`${action === "approve" ? "Approve" : "Reject"} this transfer request?`)) return;
    setBusy(request_id);
    try {
      const { data } = await api.post(`${API}/leads/transfer/requests/${request_id}/decide`, {
        action,
        decision_note: decisionNotes[request_id] || "",
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      if (data?.status === "completed") {
        toast.success(`Approved — ${data.executed_count || 0} leads moved`);
      } else if (data?.status === "rejected") {
        toast.success("Request rejected");
      } else {
        toast.success(data?.status || "Done");
      }
      onChanged && onChanged();
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    } finally {
      setBusy("");
    }
  };

  const canDecide = useMemo(() => ["admin", "md", "manager", "bdm"].includes(role), [role]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex" data-testid="transfer-approvals-drawer">
      <div className="ml-auto bg-white border-l border-slate-200 w-full max-w-2xl h-full flex flex-col">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardText size={20} className="text-[#002FA7]" />
            <h3 className="text-lg font-bold">Transfer Approvals</h3>
          </div>
          <button onClick={onClose} data-testid="approvals-close" className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>

        <div className="border-b border-slate-200 px-5 py-2 flex items-center gap-2 text-xs">
          <span className="font-bold uppercase tracking-wider text-slate-600">Filter:</span>
          {["pending", "completed", "rejected", ""].map((s) => (
            <button key={s || "all"} onClick={() => setStatusFilter(s)} data-testid={`approvals-filter-${s || "all"}`}
              className={`px-3 py-1 border ${statusFilter === s ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
              {s ? s[0].toUpperCase() + s.slice(1) : "All"}
            </button>
          ))}
          <button onClick={fetchList} className="ml-auto px-3 py-1 border border-slate-300 hover:bg-slate-50">Refresh</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="text-center text-slate-500 text-sm py-10" data-testid="approvals-loading">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-10" data-testid="approvals-empty">No transfer requests</div>
          ) : (
            items.map((r) => {
              const isPending = r.status === "pending";
              const canAct = canDecide && isPending && r.requested_by_id !== user?.user_id;
              return (
                <div key={r.transfer_request_id} className="border border-slate-200 p-3 space-y-2" data-testid={`approval-row-${r.transfer_request_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-bold text-sm text-slate-800">
                        {r.requested_by_name || "—"} <span className="text-[10px] uppercase tracking-wider text-slate-500">({(r.requested_by_role || "").toUpperCase()})</span>
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        {r.mode === "bulk" ? `Bulk · ${r.preview_count} leads` : "Single lead"} → <span className="font-bold text-slate-800">{r.to_owner_name}</span>
                      </div>
                      {r.from_owner_name && <div className="text-[11px] text-slate-500">From: {r.from_owner_name}</div>}
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${STATUS_BADGE[r.status] || "border-slate-300 text-slate-700"}`}>{r.status}</span>
                  </div>

                  <div className="text-[11px] text-slate-600">
                    Reason: <span className="font-medium">{REASON_LABELS[r.reason] || r.reason}</span>
                    {r.note && <> · Note: <span className="italic">{r.note}</span></>}
                  </div>
                  {r.executed_count > 0 && (
                    <div className="text-[11px] text-emerald-700">Executed: {r.executed_count} leads</div>
                  )}
                  {r.decided_by_name && (
                    <div className="text-[11px] text-slate-500">
                      {r.status === "rejected" ? "Rejected" : "Approved"} by {r.decided_by_name} · {(r.decided_at || "").slice(0, 19).replace("T", " ")}
                      {r.decision_note && <> · "{r.decision_note}"</>}
                    </div>
                  )}

                  {canAct && (
                    <div className="border-t pt-2 space-y-2">
                      <input type="text"
                        placeholder="Decision note (optional)"
                        value={decisionNotes[r.transfer_request_id] || ""}
                        onChange={(e) => setDecisionNotes({ ...decisionNotes, [r.transfer_request_id]: e.target.value })}
                        className="w-full rounded-none border border-slate-300 p-1.5 outline-none text-xs"
                        data-testid={`approval-note-${r.transfer_request_id}`} />
                      <div className="flex justify-end gap-2">
                        <button onClick={() => decide(r.transfer_request_id, "reject")} disabled={busy === r.transfer_request_id}
                          data-testid={`approval-reject-${r.transfer_request_id}`}
                          className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 flex items-center gap-1">
                          <XCircle size={14} /> Reject
                        </button>
                        <button onClick={() => decide(r.transfer_request_id, "approve")} disabled={busy === r.transfer_request_id}
                          data-testid={`approval-approve-${r.transfer_request_id}`}
                          className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
                          <CheckCircle size={14} /> Approve
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
